import { FormikProps } from 'formik'

import { EColumnSort } from '~/components/table/models'
import { EOnboardingStepFriendlyNames } from '~/features/onboarding/models'
import { EClientPath } from '~/models/api'
import {
  EAgentNotifyWhen,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityValues,
  EScheduleValues,
  ETextMethodValues,
} from '~/models/features'

export enum ERecordingStartTrigger {
  onConnect = 'onConnect',
  afterIntro = 'afterIntro',
}

export enum ERecordingStopTrigger {
  onDisconnect = 'onDisconnect',
  onLiveTransfer = 'onLiveTransfer',
}

export enum ESettingsStepKeys {
  creatingLibby = 'creatingLibby',
  tryItOut = 'tryItOut',
  convoOptions = 'convoOptions',
  knowledge = 'knowledge',
  notifications = 'notifications',
  chatbot = 'chatbot',
  phoneText = 'phoneText',
  email = 'email',
  escalations = 'escalations',
  apiKeys = 'apiKeys',
}

export enum ESettingsStepFriendlyNames {
  convoOptions = 'Agent Preferences',
  knowledge = 'Knowledge',
  chatbot = 'Chatbot',
  email = 'Email',
  escalations = 'Escalations (Legacy)',
  notifications = 'Notifications',
  apiKeys = 'API Keys',
  phone = 'Phone',
  text = 'Text',
  coreBehavior = 'Core Behavior',
}

export const pathByFriendlyStepName = {
  [ESettingsStepFriendlyNames.convoOptions]: EClientPath.settingsConvoOptions,
  [ESettingsStepFriendlyNames.knowledge]: EClientPath.settingsKnowledge,
  [ESettingsStepFriendlyNames.chatbot]: EClientPath.settingsChatbot,
  [ESettingsStepFriendlyNames.email]: EClientPath.settingsEmail,
  [ESettingsStepFriendlyNames.notifications]: EClientPath.settingsNotifications,
  [ESettingsStepFriendlyNames.escalations]: EClientPath.settingsEscalations,
  [ESettingsStepFriendlyNames.apiKeys]: EClientPath.settingsApiKeys,
  [ESettingsStepFriendlyNames.phone]: EClientPath.settingsPhone,
  [ESettingsStepFriendlyNames.text]: EClientPath.settingsText,
  [ESettingsStepFriendlyNames.coreBehavior]: EClientPath.settingsCoreBehavior,
}

export enum ECoreBehaviorFormKeys {
  identity = 'identity',
  rules = 'rules',
  tasks = 'tasks',
  phoneRules = 'phoneRules',
  textRules = 'textRules',
  chatRules = 'chatRules',
  emailRules = 'emailRules',
  promptOverride = 'promptOverride',
}

export enum ECoreBehaviorKeys {
  assistantName = 'assistantName',
  personality = 'personality',
  service = 'service',
  businessName = 'businessName',
  siteLink = 'siteLink',
  orgId = 'orgId',
  agentTimezone = 'agentTimezone',
  defaultCountryCode = 'defaultCountryCode',
  locationId = 'locationId',
  cta = 'cta',
  ctaText = 'ctaText',
  scheduleType = 'scheduleType',
  calendlyLink = 'calendlyLink',
  questions = 'questions',
  pricing = 'pricing',
  locationType = 'locationType',
  location = 'location',
}

export type TCoreBehaviorForm = {
  [ECoreBehaviorKeys.assistantName]: string
  [ECoreBehaviorKeys.personality]: EPersonalityValues
  [ECoreBehaviorKeys.service]: string | null
  [ECoreBehaviorKeys.businessName]: string
  [ECoreBehaviorKeys.agentTimezone]: string
  [ECoreBehaviorKeys.defaultCountryCode]: string
  [ECoreBehaviorKeys.locationId]: string
  [ECoreBehaviorKeys.cta]: string
  [ECoreBehaviorKeys.ctaText]: string | null
  [ECoreBehaviorKeys.scheduleType]: EScheduleValues
  [ECoreBehaviorKeys.calendlyLink]: string
  [ECoreBehaviorKeys.questions]: string[] | null
  [ECoreBehaviorKeys.pricing]: string
  [ECoreBehaviorKeys.locationType]: ELocationTypeValues | ''
  [ECoreBehaviorKeys.location]: string
  [ECoreBehaviorFormKeys.rules]: string
  [ECoreBehaviorFormKeys.tasks]: string[]
  [ECoreBehaviorFormKeys.phoneRules]: string
  [ECoreBehaviorFormKeys.textRules]: string
  [ECoreBehaviorFormKeys.chatRules]: string
  [ECoreBehaviorFormKeys.emailRules]: string
  [ECoreBehaviorFormKeys.promptOverride]: string
}

export enum EBasicInfoKeys {
  businessName = 'businessName',
  service = 'service',
  siteLink = 'siteLink',
  orgId = 'orgId',
  agentTimezone = 'agentTimezone',
}

export enum EKnowledgeV1Keys {
  facts = 'facts',
  siteLink = 'siteLink',
}

export type TBasicInfo = {
  [EBasicInfoKeys.businessName]: string
  [EBasicInfoKeys.service]: string
  [EBasicInfoKeys.siteLink]?: string
  [EBasicInfoKeys.agentTimezone]?: string
}

export type TBasicInfoForm = Omit<TBasicInfo, EBasicInfoKeys.service> & {
  [EBasicInfoKeys.service]: string | null
}

export type TKnowledgeV1InfoForm = {
  [EKnowledgeV1Keys.siteLink]: string
  [EKnowledgeV1Keys.facts]: string[]
}

export enum EConvoOptionsKeys {
  agentName = 'agentName',
  cta = 'cta',
  ctaText = 'ctaText',
  scheduleType = 'scheduleType',
  calendlyLink = 'calendlyLink',
  locationType = 'locationType',
  location = 'location',
  questions = 'questions',
  facts = 'facts', // todo: should be deleted once we launch knowledge feature.
  pricing = 'pricing',
  personality = 'personality',
  customInstructions = 'customInstructions',
}

export enum EEscalationsKeys {
  escalations = 'escalations',
  description = 'description',
  response = 'response',
  phoneResponse = 'phoneResponse',
  phoneTransfer = 'phoneTransfer',
}

export enum ENotificationsSettingsFormKeys {
  notifyType = 'notifyType',
  notifyEmail = 'notifyEmail',
  mobileConsent = 'mobileConsent',
  textMethod = 'textMethod',
  countryCode = 'countryCode',
  notifyMobile = 'notifyMobile',
  notifyWhen = 'notifyWhen',
}

export const NOTIFICATION_TYPE_NONE = 'none' as const

export type TNotificationTypeFormValue =
  | ENotificationType
  | typeof NOTIFICATION_TYPE_NONE

export type TNotificationsSettingsForm = {
  [ENotificationsSettingsFormKeys.notifyType]: TNotificationTypeFormValue
  [ENotificationsSettingsFormKeys.notifyEmail]: string
  [ENotificationsSettingsFormKeys.mobileConsent]: boolean
  [ENotificationsSettingsFormKeys.textMethod]: ETextMethodValues
  [ENotificationsSettingsFormKeys.countryCode]: string
  [ENotificationsSettingsFormKeys.notifyMobile]: string
  [ENotificationsSettingsFormKeys.notifyWhen]: EAgentNotifyWhen
}

export type TNotificationsEmailInputForm = {
  email: string
}

export type TNotificationsEmailInputContext = {
  emailInputFormik: FormikProps<TNotificationsEmailInputForm>
  flushPendingEmail: () => Promise<boolean>
}

export type TLink = {
  label: string
  url: string
  id: string
}

export enum ECustomInstructionsKeys {
  temperature = 'temperature',
  rulesContext = 'rulesContext',
  customQualifyInstructions = 'customQualifyInstructions',
  customSummaryInstructions = 'customSummaryInstructions',
  customNextstepsInstructions = 'customNextstepsInstructions',
}

export type TCustomInstruction = {
  temperature: number
  rulesContext: string
  customQualifyInstructions: string
  customSummaryInstructions: string
  customNextstepsInstructions: string
}

export type TConvoOptions = {
  [EConvoOptionsKeys.agentName]: string
  [EConvoOptionsKeys.cta]: string
  [EConvoOptionsKeys.ctaText]: string | null
  [EConvoOptionsKeys.scheduleType]: EScheduleValues
  [EConvoOptionsKeys.calendlyLink]: string
  [EConvoOptionsKeys.location]: string
  [EConvoOptionsKeys.locationType]: ELocationTypeValues
  [EConvoOptionsKeys.questions]: string[] | null
  [EConvoOptionsKeys.pricing]: string
  [EConvoOptionsKeys.personality]: EPersonalityValues
  [EConvoOptionsKeys.customInstructions]: Array<
    object | TCustomInstruction
  > | null
} & (
  | { [EConvoOptionsKeys.facts]: string[] }
  | { [EConvoOptionsKeys.facts]: never }
)

export type TKnowledge = {
  [EKnowledgeV1Keys.facts]: string[]
  [EKnowledgeV1Keys.siteLink]?: string
}

export enum EPhoneTextKeys {
  phoneStatus = 'phoneStatus',
  phoneVoice = 'phoneVoice',
  phoneIntroRecording = 'phoneIntroRecording',
  phoneNumber = 'phoneNumber',
  phoneIntro = 'phoneIntro',
  agentLanguage = 'agentLanguage',
  textStatus = 'textStatus',
  loopingAudio = 'loopingAudio',
  patience = 'patience',
  phoneRules = 'phoneRules',
  backupNumber = 'backupNumber',
  outageResponse = 'outageResponse',
  textFollowUpClosing = 'textFollowUpClosing',
  textRules = 'textRules',
}

export type TPhoneTextForm = {
  [EPhoneTextKeys.phoneNumber]: string
  [EPhoneTextKeys.phoneStatus]: EPhoneStatus
  [EPhoneTextKeys.agentLanguage]: string
  [EPhoneTextKeys.textStatus]: ETextStatus
  [EPhoneTextKeys.loopingAudio]: ELoopingAudio
  [EPhoneTextKeys.phoneRules]: string
  [EPhoneTextKeys.backupNumber]: string
  [EPhoneTextKeys.outageResponse]: string
  [EPhoneTextKeys.textFollowUpClosing]: string
  [EPhoneTextKeys.textRules]: string
}

export type TAreaCodeForm = {
  areaCode: string
}

export type TVoiceAndIntroForm = {
  [EPhoneTextKeys.phoneVoice]: string
  [EPhoneTextKeys.phoneIntroRecording]: string
  [EPhoneTextKeys.phoneIntro]: string
  [EPhoneTextKeys.patience]: number
}

export enum EPhoneStatus {
  active = 'active',
  disabled = 'disabled',
  lockedDisabled = 'lockedDisabled',
}

export enum EOutboundPhoneStatus {
  active = 'active',
  demoMode = 'demoMode',
  paused = 'paused',
  disabled = 'disabled',
}

export enum ETextStatus {
  active = 'active',
  disabled = 'disabled',
  settingUp = 'setting_up',
  lockedDisabled = 'lockedDisabled',
}

export enum ELoopingAudio {
  off = 'off',
  typing = 'typing',
  office = 'office',
  callCenter = 'callCenter',
  restaurant = 'restaurant',
}

export enum EPreConnectMessageSound {
  beep1 = 'beep1',
  twoTone1 = 'twoTone1',
  music1 = 'music1',
  silence = 'silence',
}

export enum EEmailStatus {
  active = 'active',
  disabled = 'disabled',
  lockedDisabled = 'lockedDisabled',
}

export enum EAgentLanguage {
  English = 'English',
  Spanish = 'Spanish',
  'Multilingual (10 languages)' = 'Multilingual',
  Bulgarian = 'Bulgarian',
  Cantonese = 'Cantonese',
  Catalan = 'Catalan',
  Czech = 'Czech',
  Danish = 'Danish',
  Dutch = 'Dutch',
  Estonian = 'Estonian',
  Finnish = 'Finnish',
  Flemish = 'Flemish',
  French = 'French',
  German = 'German',
  'German (Switzerland)' = 'German_Switzerland',
  Greek = 'Greek',
  Hindi = 'Hindi',
  Hungarian = 'Hungarian',
  Indonesian = 'Indonesian',
  Italian = 'Italian',
  Japanese = 'Japanese',
  Korean = 'Korean',
  Latvian = 'Latvian',
  Lithuanian = 'Lithuanian',
  Malay = 'Malay',
  'Mandarin (Simplified)' = 'Mandarin_Simplified',
  'Mandarin (Traditional)' = 'Mandarin_Traditional',
  Norwegian = 'Norwegian',
  Polish = 'Polish',
  Portuguese = 'Portuguese',
  Romanian = 'Romanian',
  Russian = 'Russian',
  Slovak = 'Slovak',
  Swedish = 'Swedish',
  Thai = 'Thai',
  Turkish = 'Turkish',
  Ukrainian = 'Ukrainian',
  Vietnamese = 'Vietnamese',
}

export type TEscalations = {
  [EEscalationsKeys.escalations]: string[] | null
  [EEscalationsKeys.description]: string
  [EEscalationsKeys.response]: string
  [EEscalationsKeys.phoneResponse]: string
  [EEscalationsKeys.phoneTransfer]: string
}

export type TPhoneAndTextEntireForm = TPhoneTextForm &
  TAreaCodeForm &
  TVoiceAndIntroForm
// TODO: remove Partial<{[key in ...]: ...}> after all steps are implemented
export type TSettingsDefinitionSteps = {
  [EOnboardingStepFriendlyNames.creatingLibby]: boolean
  [ESettingsStepFriendlyNames.convoOptions]: Partial<TConvoOptions> | null
  [ESettingsStepFriendlyNames.knowledge]: Partial<TKnowledge> | null
  [ESettingsStepFriendlyNames.escalations]: TEscalations | null
} & Partial<{
  [key in ESettingsStepFriendlyNames]: unknown
}>

export enum EEmail {
  emailStatus = 'emailStatus',
  agentEmail = 'agentEmail',
  signature = 'signature',
  isDefaultSignature = 'isDefaultSignature',
  emailRules = 'emailRules',
  agentEmailCustom = 'agentEmailCustom',
}
export type TEmail = {
  [EEmail.emailStatus]: EEmailStatus
  [EEmail.agentEmail]?: string
  [EEmail.signature]: string
  [EEmail.isDefaultSignature]: boolean
  [EEmail.emailRules]?: string
}

export enum EChatbotDetails {
  chatIntro = 'chatIntro',
  chatbotImgURL = 'chatbotImgURL',
  showLibby = 'showLibby',
  showBranding = 'showBranding',
  chatFriendlyUrl = 'chatFriendlyUrl',
  chatRules = 'chatRules',
  chatTitle = 'chatTitle',
  chatSubtitle = 'chatSubtitle',
  layout = 'layout',
  chatStatus = 'chatStatus',
  chatbotImgFileURL = 'chatbotImgFileURL',
  embedIconBGColor = 'embedIconBGColor',
  embedIconColor = 'embedIconColor',
  embedIconShape = 'embedIconShape',
  embedPopupAutoCloseDelaySeconds = 'embedPopupAutoCloseDelaySeconds',
  embedPopupAutoCloseEnabled = 'embedPopupAutoCloseEnabled',
  embedPopupDelaySeconds = 'embedPopupDelaySeconds',
  embedPopupDevices = 'embedPopupDevices',
  embedPopupEnabled = 'embedPopupEnabled',
  embedPopupMessage = 'embedPopupMessage',
  embedPopupCustomImageUrl = 'embedPopupCustomImageUrl',
  embedPopupCustomImageFileUrl = 'embedPopupCustomImageFileUrl',
  customLinks = 'customLinks',
  preChatFormEnabled = 'preChatFormEnabled',
  formId = 'formId',
}

export type TChatbotSettingsDetails = {
  [EChatbotDetails.chatIntro]?: string
  [EChatbotDetails.chatbotImgURL]?: string | null
  [EChatbotDetails.showLibby]?: boolean
  [EChatbotDetails.showBranding]?: boolean
  [EChatbotDetails.chatFriendlyUrl]?: string | null
  [EChatbotDetails.chatRules]?: string
  [EChatbotDetails.chatTitle]: string
  [EChatbotDetails.chatSubtitle]: string
}

export enum EChatbotSelection {
  theme = 'theme',
  linkView = 'Link View',
  simple = 'Simple',
}
export enum EChatbotTheme {
  linkView = 'Link View',
  simple = 'Simple',
}
export type TChatbotSettingsLayout = {
  selection: TChatbotSettingsLayoutSelection
  options: TChatbotSettingsLayoutOptions
}

export type TChatbotSettingsLayoutCustomSelection = {
  [key in EChatbotTheme]: boolean
}

export type TChatbotSettingsLayoutSelection = {
  theme: EChatbotTheme
  custom: TChatbotSettingsLayoutCustomSelection
  [EChatbotTheme.linkView]: EChatbotColorPalette
  [EChatbotTheme.simple]: string
}
export type TChatbotSettingsLayoutOptions = {
  [EChatbotTheme.linkView]: TChatbotLinkViewOptions
  [EChatbotTheme.simple]: string[]
}
export type TChatbotLinkViewOptions = {
  [key in EChatbotColorPalette]: TChatbotColorPalette
}

export enum EChatbotColorPalette {
  teal = 'Teal',
  dark = 'Dark',
  purple = 'Purple',
  neutral = 'Neutral',
}

export type TChatbotColorPalette = {
  mainBackgroundColor: string
  buttonColor: string
  buttonTextColor: string
  accentColor: string
  secondaryTextColor: string
}

export type TColorPalette = {
  [key: string]: TColorPaletteSections
}
export type TColorPaletteSections = {
  mainBackgroundColor: string
  buttonColor: string
  buttonTextColor: string
  accentColor: string
  secondaryTextColor: string
}
export enum EColorPaletteSectionsKeys {
  mainBackgroundColor = 'mainBackgroundColor',
  buttonColor = 'buttonColor',
  buttonTextColor = 'buttonTextColor',
  accentColor = 'accentColor',
  secondaryTextColor = 'secondaryTextColor',
}
export enum EColorPaletteSections {
  mainBackgroundColor = 'Main background color',
  buttonColor = 'Button color',
  buttonTextColor = 'Button text color',
  accentColor = 'Accent color',
  secondaryTextColor = 'Secondary text color',
}

export type TEscalation = {
  description: string
  response: string
  phoneResponse: string
  phoneTransfer: string
  id: string
}

export type TEscalationWithTransferIndex = {
  description: string
  response: string
  phoneResponse: string
  phoneTransfer: string
  phoneTransferIndex: number
  id: string
}
export type TEscalationForm = {
  description: string
  response: string
  phoneResponse: string
  phoneTransfer: string
}

// API Keys types
export type TApiKey = {
  id: string
  name: string
  orgId: string
  apiKey: string // Backend returns this as masked value in GET requests, full value in POST
  status: string | null
  usagePlan: string | null
  expiresAt: string | null
  createdAt: string // Backend uses createdAt
  updatedDate: string
  Org: {
    id: string
    referralCode: string | null
    paymentTier: string
  }
}

export type TCreateApiKeyPayload = {
  name: string
  orgId: string
  expiresAt?: string
}

export type TCreateApiKeyResponse = {
  success: boolean
  data: TApiKey
  apiKey: string // Full key for one-time display (maps to gatewayApiKeyId from DB)
}

// TODO: Move original enum to a individual file to avoid circular dependencies
enum EChatStatus {
  active = 'active',
  paused = 'paused',
  disabled = 'disabled',
  lockedDisabled = 'lockedDisabled',
}

// TODO: same for these enums
export enum EIconShape {
  square = 'square',
  circle = 'circle',
}

export enum EPopupDevices {
  desktop = 'desktop',
  mobile = 'mobile',
}

export enum EPreviewTab {
  linkView = 'Link View',
  simple = 'Simple',
  autoOpen = 'Auto-Open',
}

export type TChatbotSettingsForm = {
  [EChatbotDetails.chatFriendlyUrl]: string
  [EChatbotDetails.layout]: TChatbotSettingsLayout
  [EChatbotDetails.chatbotImgURL]: string | File | null
  [EChatbotDetails.showBranding]: boolean
  [EChatbotDetails.chatTitle]: string
  [EChatbotDetails.chatSubtitle]: string
  [EChatbotDetails.chatIntro]: string
  [EChatbotDetails.chatStatus]: EChatStatus
  [EChatbotDetails.chatbotImgFileURL]: string
  [EChatbotDetails.embedIconBGColor]: string
  [EChatbotDetails.embedIconColor]: string
  [EChatbotDetails.embedIconShape]: EIconShape | null
  [EChatbotDetails.embedPopupAutoCloseDelaySeconds]: number | ''
  [EChatbotDetails.embedPopupAutoCloseEnabled]: boolean
  [EChatbotDetails.embedPopupDelaySeconds]: number
  [EChatbotDetails.embedPopupDevices]: EPopupDevices[]
  [EChatbotDetails.embedPopupEnabled]: boolean
  [EChatbotDetails.embedPopupMessage]: string
  [EChatbotDetails.embedPopupCustomImageUrl]: string | File | null
  [EChatbotDetails.embedPopupCustomImageFileUrl]: string
  [EChatbotDetails.customLinks]: TLink[]
  [EChatbotDetails.preChatFormEnabled]: boolean
  [EChatbotDetails.formId]: string
}

export type TEmailSettingsForm = {
  [EEmail.agentEmail]: string
  [EEmail.agentEmailCustom]: string
  [EEmail.emailStatus]: EEmailStatus
  [EEmail.isDefaultSignature]: boolean
  [EEmail.signature]: string
}

export enum ETextSettingsKeys {
  phoneNumber = 'phoneNumber',
  textStatus = 'textStatus',
  textRules = 'textRules',
}

export type TTextSettingsForm = {
  [ETextSettingsKeys.phoneNumber]: string
  [ETextSettingsKeys.textStatus]: ETextStatus
  [ETextSettingsKeys.textRules]: string
}

export enum EPhoneKeys {
  phoneNumber = 'phoneNumber',
  phoneLanguage = 'phoneLanguage',
  phoneStatus = 'phoneStatus',
  phoneVoice = 'phoneVoice',
  textStatus = 'textStatus',
  loopingAudio = 'loopingAudio',
  patience = 'patience',
  phoneRules = 'phoneRules',
  outageResponse = 'outageResponse',
  backupNumber = 'backupNumber',
  textFollowUpClosing = 'textFollowUpClosing',
  textFollowUpTemplate = 'textFollowUpTemplate',
  phoneIntro = 'phoneIntro',
  playRecording = 'playRecording',
  recordingEnabled = 'recordingEnabled',
  recordingStartTrigger = 'recordingStartTrigger',
  recordingStopTrigger = 'recordingStopTrigger',
  allowIntroInterrupt = 'allowIntroInterrupt',
  preConnectMessageEnabled = 'preConnectMessageEnabled',
  preConnectMessageText = 'preConnectMessageText',
  preConnectMessageVoiceId = 'preConnectMessageVoiceId',
  preConnectMessageSound = 'preConnectMessageSound',
  llmModel = 'llmModel',
  ttsProvider = 'ttsProvider',
  ttsSpeed = 'ttsSpeed',
  expressiveMode = 'expressiveMode',
  phoneOrchestrationType = 'phoneOrchestrationType',
}

export type TPhoneSettingsForm = {
  [EPhoneKeys.phoneNumber]: string
  [EPhoneKeys.phoneLanguage]: EAgentLanguage
  [EPhoneKeys.phoneStatus]: EPhoneStatus
  [EPhoneKeys.phoneVoice]: string
  [EPhoneKeys.textStatus]: ETextStatus
  [EPhoneKeys.loopingAudio]: ELoopingAudio
  [EPhoneKeys.patience]: number
  [EPhoneKeys.outageResponse]: string
  [EPhoneKeys.backupNumber]: string
  [EPhoneKeys.textFollowUpClosing]: string
  [EPhoneKeys.textFollowUpTemplate]?: string
  [EPhoneKeys.phoneIntro]: string
  [EPhoneKeys.playRecording]: boolean
  [EPhoneKeys.allowIntroInterrupt]: boolean
  [EPhoneKeys.preConnectMessageEnabled]: boolean
  [EPhoneKeys.preConnectMessageText]: string
  [EPhoneKeys.preConnectMessageVoiceId]: string
  [EPhoneKeys.preConnectMessageSound]: EPreConnectMessageSound
  [EPhoneKeys.recordingEnabled]: boolean
  [EPhoneKeys.recordingStartTrigger]: ERecordingStartTrigger
  [EPhoneKeys.recordingStopTrigger]: ERecordingStopTrigger
  [EPhoneKeys.llmModel]: string
  [EPhoneKeys.ttsProvider]: string
  [EPhoneKeys.ttsSpeed]: number
  [EPhoneKeys.expressiveMode]: boolean
  [EPhoneKeys.phoneOrchestrationType]: EOrchestratorType
}

export type TPhoneSettingsFormStatus = {
  pendingIntroRecord?: boolean
  setPendingIntroRecord?: (pending: boolean) => void
}

export enum EEmailAddressAsignmentAction {
  assign = 'assign',
  disable = 'disable',
  cancel = 'cancel',
}

// Phone orchestrator types for admin phone migration
export enum EOrchestratorType {
  legacy = 'legacy', // Legacy phone system (Twilio webhook)
  standard = 'standard', // SIP + cascaded LiveKit agent
  realtime = 'realtime', // SIP + GPT-Live duplex agent
}

export type TMigratePhoneNumberRequest = {
  phoneNumber: string
  fromOrchestrator?: EOrchestratorType
  toOrchestrator?: EOrchestratorType
}

export type TMigratePhoneNumberResult = {
  phoneNumber: string
  fromOrchestrator: EOrchestratorType
  toOrchestrator: EOrchestratorType
  status: 'success' | 'error'
  error?: string
}

export type TMigratePhoneNumberResponse = {
  results: TMigratePhoneNumberResult[]
  total: number
  successful: number
  failed: number
}

export enum EProvisionPhoneNumberSource {
  database = 'database',
  twilio = 'twilio',
}

export enum EProvisionPhoneNumberStatus {
  success = 'success',
  dryRun = 'dry_run',
  failed = 'failed',
}

export type TProvisionPhoneNumberItem = {
  areaCode: string
  agentId: string
  orchestratorType?: EOrchestratorType
}

export type TProvisionPhoneNumberPayload = {
  items: TProvisionPhoneNumberItem[]
  country?: string
  orchestratorType?: EOrchestratorType
  dryRun?: boolean
}

export type TProvisionPhoneNumberResult = {
  areaCode: string
  agentId: string
  phoneNumber: string | null
  status: EProvisionPhoneNumberStatus
  source: EProvisionPhoneNumberSource | null
  orchestratorType: EOrchestratorType
  error: string | null
  sid: string | null
  friendlyName: string | null
  voiceUrl: string | null
  smsUrl: string | null
  trunkSid: string | null
  locality: string | null
  region: string | null
  message: string | null
}

export type TProvisionPhoneNumberResponse = {
  results: TProvisionPhoneNumberResult[]
  total: number
  successful: number
  failed: number
  dryRun: boolean
}

export enum EDeprovisionIdentifierType {
  agentId = 'agent_id',
  phoneNumber = 'phone_number',
}

export enum EDeprovisionStatus {
  success = 'success',
  failed = 'failed',
}

export type TDeprovisionPhoneNumberPayload = {
  agentIds?: string[]
  phoneNumbers?: string[]
}

export type TDeprovisionPhoneNumberResult = {
  identifier: string
  identifierType: EDeprovisionIdentifierType
  phoneNumber: string | null
  status: EDeprovisionStatus
  error: string | null
  releasedFromTwilio: boolean
  removedFromAgent: boolean
  deletedFromDb: boolean
}

export type TDeprovisionPhoneNumberResponse = {
  results: TDeprovisionPhoneNumberResult[]
  total: number
  successful: number
  failed: number
}

export type TPhoneNumberStatusResponse = {
  phoneNumber: string
  agentId?: string
  agentName?: string
  status: 'assigned' | 'available'
  orchestratorType?: EOrchestratorType
  createdAt?: string
  updatedAt?: string
}

export type TOrgPronunciationRule = {
  word: string
  pronunciation: string
  type?: string
}

export type TOrgPronunciationDictionary = {
  orgId: string
  name: string | null
  rules: TOrgPronunciationRule[]
}

export enum EAnalysisSettingsFiltersFormKeys {
  search = 'search',
  source = 'source',
  destination = 'destination',
  timing = 'timing',
}

export enum EAnalysisFieldSourceFilter {
  system = 'system',
  custom = 'custom',
  all = 'all',
}

export enum EAnalysisFieldDestinationTable {
  contact = 'Contact',
  conversation = 'Conversation',
}

export enum EAnalysisFieldDestinationFilter {
  contact = EAnalysisFieldDestinationTable.contact,
  conversation = EAnalysisFieldDestinationTable.conversation,
  all = 'all',
}

export enum EAnalysisFieldExecutionTiming {
  sync = 'sync',
  async = 'async',
}

export enum EAnalysisFieldTimingFilter {
  realTime = EAnalysisFieldExecutionTiming.sync,
  background = EAnalysisFieldExecutionTiming.async,
  all = 'all',
}

export type TAnalysisSettingsFiltersForm = {
  [EAnalysisSettingsFiltersFormKeys.search]: string
  [EAnalysisSettingsFiltersFormKeys.source]: EAnalysisFieldSourceFilter
  [EAnalysisSettingsFiltersFormKeys.destination]: EAnalysisFieldDestinationFilter
  [EAnalysisSettingsFiltersFormKeys.timing]: EAnalysisFieldTimingFilter
}

export enum EAnalysisSettingsTabs {
  fields = 'fields',
  assistants = 'assistant rules',
}

export type TAnalysisSettingsLocationState = {
  analysisFieldId?: string
}

export enum EAnalysisFieldsTabStatus {
  active = 'active',
  inactive = 'inactive',
}

export enum EAnalysisFieldsStatusFilter {
  active = 'active',
  disabled = 'disabled',
  all = 'all',
}

export enum EAnalysisFieldsSortBy {
  name = 'name',
  executionTiming = 'executionTiming',
  status = 'status',
  ruleCount = 'ruleCount',
  assignedAssistantCount = 'assignedAssistantCount',
}

export enum EAnalysisFieldDataType {
  text = 'text',
  number = 'number',
  boolean = 'boolean',
  date = 'date',
  time = 'time',
  datetime = 'datetime',
  object = 'object',
}

export enum EAnalysisFieldDestinationType {
  column = 'column',
  sharedJson = 'sharedJson',
  recordList = 'recordList',
}

export enum EAnalysisFieldStatus {
  active = 'active',
  disabled = 'disabled',
}

export enum EAnalysisFieldUpdateStrategy {
  replace = 'replace',
  append = 'append',
  fillIfEmpty = 'fillIfEmpty',
}

export type TGetAnalysisFieldsFilters = {
  search?: string
  fieldType?: EAnalysisFieldSourceFilter
  status?: EAnalysisFieldsStatusFilter
  executionTiming?: EAnalysisFieldTimingFilter
  page?: number
  pageSize?: number
  sortBy?: EAnalysisFieldsSortBy
  sortDirection?: EColumnSort
}

export enum EAnalysisFieldListItemKeys {
  id = 'id',
  key = 'key',
  name = 'name',
  description = 'description',
  isSystemField = 'isSystemField',
  fieldType = 'fieldType',
  destinationTable = 'destinationTable',
  destinationType = 'destinationType',
  executionTiming = 'executionTiming',
  status = 'status',
  updateStrategy = 'updateStrategy',
  ruleCount = 'ruleCount',
  activeRuleCount = 'activeRuleCount',
  hasOrgDefault = 'hasOrgDefault',
  orgDefaultRuleId = 'orgDefaultRuleId',
  assistantOverrideCount = 'assistantOverrideCount',
  assignedAssistantCount = 'assignedAssistantCount',
  outputSchema = 'outputSchema',
  defaultSystemPromptId = 'defaultSystemPromptId',
}

export type TAnalysisFieldListItem = {
  [EAnalysisFieldListItemKeys.id]: string
  [EAnalysisFieldListItemKeys.key]: string
  [EAnalysisFieldListItemKeys.name]: string
  [EAnalysisFieldListItemKeys.description]: string | null
  [EAnalysisFieldListItemKeys.isSystemField]: boolean
  [EAnalysisFieldListItemKeys.fieldType]: EAnalysisFieldDataType
  [EAnalysisFieldListItemKeys.destinationTable]: EAnalysisFieldDestinationTable
  [EAnalysisFieldListItemKeys.destinationType]: EAnalysisFieldDestinationType
  [EAnalysisFieldListItemKeys.executionTiming]: EAnalysisFieldExecutionTiming
  [EAnalysisFieldListItemKeys.status]: EAnalysisFieldStatus
  [EAnalysisFieldListItemKeys.updateStrategy]: EAnalysisFieldUpdateStrategy | null
  [EAnalysisFieldListItemKeys.ruleCount]: number
  [EAnalysisFieldListItemKeys.activeRuleCount]: number
  [EAnalysisFieldListItemKeys.hasOrgDefault]: boolean
  [EAnalysisFieldListItemKeys.orgDefaultRuleId]: string | null
  [EAnalysisFieldListItemKeys.assistantOverrideCount]: number
  [EAnalysisFieldListItemKeys.assignedAssistantCount]: number
}

export type TAnalysisFieldDefaultSystemPrompt = {
  id: string
  prompt: string
}

export type TAnalysisFieldDetail = {
  [EAnalysisFieldListItemKeys.id]: string
  [EAnalysisFieldListItemKeys.key]: string
  [EAnalysisFieldListItemKeys.name]: string
  [EAnalysisFieldListItemKeys.description]: string | null
  [EAnalysisFieldListItemKeys.isSystemField]: boolean
  [EAnalysisFieldListItemKeys.fieldType]: EAnalysisFieldDataType
  [EAnalysisFieldListItemKeys.destinationTable]: EAnalysisFieldDestinationTable
  [EAnalysisFieldListItemKeys.destinationType]: EAnalysisFieldDestinationType
  [EAnalysisFieldListItemKeys.executionTiming]: EAnalysisFieldExecutionTiming
  [EAnalysisFieldListItemKeys.status]: EAnalysisFieldStatus
  [EAnalysisFieldListItemKeys.updateStrategy]: EAnalysisFieldUpdateStrategy | null
  [EAnalysisFieldListItemKeys.outputSchema]: unknown | null
  [EAnalysisFieldListItemKeys.defaultSystemPromptId]: string | null
  defaultSystemPrompt?: TAnalysisFieldDefaultSystemPrompt | null
}

export enum EAnalysisFieldRuleKeys {
  id = 'id',
  name = 'name',
  description = 'description',
  prompt = 'prompt',
  isOrgDefault = 'isOrgDefault',
  assignedAssistantCount = 'assignedAssistantCount',
  assistantOverrideCount = 'assistantOverrideCount',
  assistants = 'assistants',
}

export type TAnalysisFieldRuleAssistant = {
  id: string
  agentReferenceName: string | null
}

export type TAnalysisFieldRule = {
  [EAnalysisFieldRuleKeys.id]: string
  [EAnalysisFieldRuleKeys.name]: string
  [EAnalysisFieldRuleKeys.description]: string | null
  [EAnalysisFieldRuleKeys.prompt]: string
  [EAnalysisFieldRuleKeys.isOrgDefault]: boolean
  [EAnalysisFieldRuleKeys.assignedAssistantCount]: number
  [EAnalysisFieldRuleKeys.assistantOverrideCount]: number
  [EAnalysisFieldRuleKeys.assistants]?: TAnalysisFieldRuleAssistant[]
}

export enum EAnalysisFieldChipType {
  sourceSystem = 'sourceSystem',
  sourceCustom = 'sourceCustom',
  systemDefaultRule = 'systemDefaultRule',
  orgDefaultRule = 'orgDefaultRule',
  assistantOverrideRule = 'assistantOverrideRule',
  timingRealTime = 'timingRealTime',
  timingBackground = 'timingBackground',
}

export type TAnalysisFieldDetailSyncLimitMeta = {
  customSyncFieldCount: number
  customSyncFieldLimit: number
  canCreateMoreSyncFields: boolean
}

export type TAnalysisFieldDetailSummary = {
  ruleCount: number
  activeRuleCount: number
  hasOrgDefault: boolean
  orgDefaultRuleId: string | null
  syncLimitMeta?: TAnalysisFieldDetailSyncLimitMeta
}

export type TGetAnalysisFieldResponse = {
  field: TAnalysisFieldDetail
  rules: TAnalysisFieldRule[]
  summary: TAnalysisFieldDetailSummary
}

export type TGetAnalysisFieldsForAssistantResponse = {
  items: TGetAnalysisFieldResponse[]
}

export enum EAnalysisCreateFieldFormKeys {
  name = EAnalysisFieldListItemKeys.name,
  key = EAnalysisFieldListItemKeys.key,
  description = EAnalysisFieldListItemKeys.description,
  fieldType = EAnalysisFieldListItemKeys.fieldType,
  destinationTable = EAnalysisFieldListItemKeys.destinationTable,
  updateStrategy = EAnalysisFieldListItemKeys.updateStrategy,
  executionTiming = EAnalysisFieldListItemKeys.executionTiming,
}

export type TAnalysisCreateFieldForm = {
  [EAnalysisCreateFieldFormKeys.name]: string
  [EAnalysisCreateFieldFormKeys.key]: string
  [EAnalysisCreateFieldFormKeys.description]: string
  [EAnalysisCreateFieldFormKeys.fieldType]: EAnalysisFieldDataType
  [EAnalysisCreateFieldFormKeys.destinationTable]: EAnalysisFieldDestinationTable
  [EAnalysisCreateFieldFormKeys.updateStrategy]: EAnalysisFieldUpdateStrategy
  [EAnalysisCreateFieldFormKeys.executionTiming]: EAnalysisFieldExecutionTiming
}

export enum EAnalysisEditFieldFormKeys {
  name = EAnalysisFieldListItemKeys.name,
  key = EAnalysisFieldListItemKeys.key,
  description = EAnalysisFieldListItemKeys.description,
  fieldType = EAnalysisFieldListItemKeys.fieldType,
  destinationTable = EAnalysisFieldListItemKeys.destinationTable,
  updateStrategy = EAnalysisFieldListItemKeys.updateStrategy,
  executionTiming = EAnalysisFieldListItemKeys.executionTiming,
}

export type TAnalysisEditFieldForm = {
  [EAnalysisEditFieldFormKeys.name]: string
  [EAnalysisEditFieldFormKeys.key]: string
  [EAnalysisEditFieldFormKeys.description]: string
  [EAnalysisEditFieldFormKeys.fieldType]: EAnalysisFieldDataType
  [EAnalysisEditFieldFormKeys.destinationTable]: EAnalysisFieldDestinationTable
  [EAnalysisEditFieldFormKeys.updateStrategy]: EAnalysisFieldUpdateStrategy | null
  [EAnalysisEditFieldFormKeys.executionTiming]: EAnalysisFieldExecutionTiming
}

export type TAnalysisFieldsPagination = {
  page: number
  pageSize: number
  total: number
}

export type TAnalysisFieldsSummary = {
  customSyncFieldCount: number
  customSyncFieldLimit: number
  canCreateMoreSyncFields: boolean
}

export type TGetAnalysisFieldsResponse = {
  items: TAnalysisFieldListItem[]
  pagination: TAnalysisFieldsPagination
  summary: TAnalysisFieldsSummary
}

export type TCreateAnalysisFieldPayload = {
  orgId: string
  [EAnalysisFieldListItemKeys.name]: string
  [EAnalysisFieldListItemKeys.key]: string
  [EAnalysisFieldListItemKeys.fieldType]: EAnalysisFieldDataType
  [EAnalysisFieldListItemKeys.destinationTable]: EAnalysisFieldDestinationTable
  [EAnalysisFieldListItemKeys.destinationType]: EAnalysisFieldDestinationType
  [EAnalysisFieldListItemKeys.executionTiming]: EAnalysisFieldExecutionTiming
  [EAnalysisFieldListItemKeys.updateStrategy]: EAnalysisFieldUpdateStrategy
  [EAnalysisFieldListItemKeys.description]?: string
}

export type TCreateAnalysisFieldResponse = {
  field: TAnalysisFieldListItem
  initialRule: {
    id: string
    orgId: string
    analysisFieldId: string
    name: string
    description: string | null
    prompt: string
    createdDate: string
    updatedDate: string
  } | null
  orgDefaultSet: boolean
  assignmentSummary: { assignedAssistantCount: number } | null
}

export type TUpdateAnalysisFieldResponse = {
  field: TAnalysisFieldListItem
}

export type TUpdateAnalysisFieldPayload = {
  [EAnalysisFieldListItemKeys.name]?: string
  [EAnalysisFieldListItemKeys.description]?: string | null
  [EAnalysisFieldListItemKeys.executionTiming]?: EAnalysisFieldExecutionTiming
  [EAnalysisFieldListItemKeys.updateStrategy]?: EAnalysisFieldUpdateStrategy
  [EAnalysisFieldListItemKeys.status]?: EAnalysisFieldStatus
}

export type TUpdateAnalysisFieldVariables = {
  fieldId: string
} & TUpdateAnalysisFieldPayload

export enum EAnalysisFieldAddEditRuleFormKeys {
  ruleName = EAnalysisFieldRuleKeys.name,
  prompt = EAnalysisFieldRuleKeys.prompt,
}

export type TAnalysisFieldAddEditRuleForm = {
  [EAnalysisFieldAddEditRuleFormKeys.ruleName]: string
  [EAnalysisFieldAddEditRuleFormKeys.prompt]: string
}

export type TAnalysisFieldPromptRecord = {
  id: string
  orgId: string
  analysisFieldId: string
  name: string
  description: string | null
  prompt: string
  createdDate: string
  updatedDate: string
}

export type TCreateAnalysisFieldRuleAssignments = {
  setAsOrgDefault?: boolean
  assignAssistantIds?: string[]
}

export type TCreateAnalysisFieldRulePayload = {
  name: string
  prompt: string
  description?: string
  assignments?: TCreateAnalysisFieldRuleAssignments
}

export type TCreateAnalysisFieldRuleResponse = {
  rule: TAnalysisFieldPromptRecord
  orgDefaultSet: boolean
  assignmentSummary: { assignedAssistantCount: number } | null
}

export type TUpdateAnalysisFieldRulePayload = {
  name?: string
  description?: string | null
  prompt?: string
}

export type TUpdateAnalysisFieldRuleResponse = {
  rule: TAnalysisFieldPromptRecord
}

export enum EAssignAssistantsFormKeys {
  agentsIds = 'agentsIds',
}

export type TAssignAssistantsForm = {
  [EAssignAssistantsFormKeys.agentsIds]: string[]
}

export type TBulkAssignAnalysisFieldRuleAssistantsPayload = {
  addAgentIds?: string[]
  removeAgentIds?: string[]
}

export type TBulkAssignAnalysisFieldRuleAssistantsResponse = {
  added: number
  removed: number
  alreadyAssigned: number
  alreadyRemoved: number
}

export type TDuplicateAnalysisFieldRulePayload = {
  orgId: string
  name?: string
}

export type TDuplicateAnalysisFieldRuleResponse = {
  rule: TAnalysisFieldPromptRecord
}

export type TSetAnalysisFieldOrgDefaultPayload = {
  ruleId: string
}

export type TSetAnalysisFieldOrgDefaultResponse = {
  assignment: {
    id: string
    orgId: string
    analysisFieldId: string
    analysisFieldPromptId: string
    scopeType: string
    agentId: string | null
    createdDate: string
    updatedDate: string
  }
  previousRuleId: string | null
  changed: boolean
}

export type TDeleteAnalysisFieldRuleResponse = {
  deletedRule: {
    id: string
    name: string
  }
  impact: {
    wasOrgDefault: boolean
    orgDefaultRemoved: boolean
    agentAssignmentsRemoved: number
    remainingRuleCount: number
  }
}
